<script setup>
/**
 * GlDisclosureDropdown — a manual @gitlab/ui-compatible disclosure dropdown.
 *
 * Supports the most common props and the `toggle` slot.
 * Items can be plain objects `{ text }` or groups `{ name, items }`.
 *
 * Props mirror the upstream GlDisclosureDropdown:
 *   - `items`       (Array)   — list items or groups ({ name, items })
 *   - `toggleText`  (String)  — accessible toggle button text
 *   - `icon`        (String)  — icon name for the toggle button
 *   - `noCaret`     (Boolean) — hide the dropdown caret
 *   - `placement`   (String)  — dropdown alignment ("bottom-start" | "bottom-end")
 *   - `fluidWidth`  (Boolean) — let dropdown width match content
 *   - `category`    (String)  — button category ("primary" | "secondary" | "tertiary")
 *   - `variant`     (String)  — button variant ("default" | "confirm" | "danger")
 *   - `textSrOnly`  (Boolean) — visually hide toggle text
 */
import { onBeforeUnmount, ref, useSlots } from "vue";
import { iconHref } from "~/utils/icons.js";

defineProps({
  category: { default: "primary", type: String },
  fluidWidth: { default: false, type: Boolean },
  icon: { default: "", type: String },
  items: { default: () => [], type: Array },
  noCaret: { default: false, type: Boolean },
  placement: { default: "bottom-start", type: String },
  textSrOnly: { default: false, type: Boolean },
  toggleClass: { default: "", type: [String, Array, Object] },
  toggleText: { default: "", type: String },
  variant: { default: "default", type: String }
});

const emit = defineEmits(["shown", "hidden", "action"]);
const slots = useSlots();

const isOpen = ref(false);
const dropdownRef = ref(null);

function isGroup(item) {
  return Array.isArray(item?.items);
}

function toggle() {
  if (isOpen.value) {
    close();
  } else {
    open();
  }
}

function open() {
  isOpen.value = true;
  document.addEventListener("click", handleOutsideClick, true);
  document.addEventListener("keydown", handleEscape);
  emit("shown");
}

function close() {
  if (!isOpen.value) return;
  isOpen.value = false;
  document.removeEventListener("click", handleOutsideClick, true);
  document.removeEventListener("keydown", handleEscape);
  emit("hidden");
}

function handleOutsideClick(event) {
  if (dropdownRef.value && !dropdownRef.value.contains(event.target)) {
    close();
  }
}

function handleEscape(event) {
  if (event.key === "Escape") {
    close();
  }
}

function handleAction(item) {
  emit("action", item);
  close();
}

onBeforeUnmount(() => {
  document.removeEventListener("click", handleOutsideClick, true);
  document.removeEventListener("keydown", handleEscape);
});

defineExpose({ close, open });
</script>

<template>
  <div ref="dropdownRef" class="relative inline-block">
    <!-- Toggle -->
    <slot v-if="slots.toggle" name="toggle" :toggle="toggle" :is-open="isOpen" />
    <button
      v-else
      type="button"
      :aria-expanded="isOpen ? 'true' : 'false'"
      :aria-label="textSrOnly ? toggleText : undefined"
      :class="[
        'inline-flex items-center gap-1 rounded-[var(--gl-action-border-radius)] text-sm',
        category === 'tertiary'
          ? 'border-transparent bg-transparent px-2 py-1 text-[var(--gl-text-color-default)] hover:bg-[var(--gl-background-color-strong)]'
          : 'border border-[var(--gl-button-default-secondary-border-color-default)] bg-[var(--gl-background-color-default)] px-3 py-1.5 text-[var(--gl-text-color-default)] hover:bg-[var(--gl-background-color-subtle)]',
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--gl-focus-ring-outer-color)]',
        toggleClass
      ]"
      @click.stop="toggle"
    >
      <svg v-if="icon" class="h-4 w-4" aria-hidden="true">
        <use :href="iconHref(icon)" />
      </svg>
      <span v-if="toggleText && !textSrOnly">{{ toggleText }}</span>
      <svg v-if="!noCaret" class="h-3 w-3" aria-hidden="true">
        <use :href="iconHref('chevron-down')" />
      </svg>
    </button>

    <!-- Dropdown panel -->
    <div
      v-if="isOpen"
      :class="[
        'absolute z-50 mt-3 rounded-lg border border-[var(--gl-border-color-default)] bg-[var(--gl-background-color-default)] py-2 shadow-lg',
        placement === 'bottom-end' ? 'right-0' : 'left-0',
        fluidWidth ? 'w-max max-w-xs' : 'min-w-[12rem]'
      ]"
      role="menu"
    >
      <!-- Arrow -->
      <div
        :class="[
          'absolute -top-[6px] h-3 w-3 rotate-45 border-l border-t border-[var(--gl-border-color-default)] bg-[var(--gl-background-color-default)]',
          placement === 'bottom-end' ? 'right-3' : 'left-3'
        ]"
      />
      <slot>
        <template v-for="(item, index) in items" :key="index">
          <!-- Group -->
          <template v-if="isGroup(item)">
            <div v-if="index > 0" class="my-2 border-t border-[var(--gl-border-color-default)]" role="separator" />
            <div v-if="item.name" class="px-4 py-2 text-xs text-[var(--gl-text-color-subtle)]">
              {{ item.name }}
            </div>
            <ul class="m-0 list-none p-0" role="group">
              <li v-for="(groupItem, gIndex) in item.items" :key="gIndex" role="menuitem">
                <a
                  v-if="groupItem.href"
                  :href="groupItem.href"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="block px-4 py-2 text-sm text-[var(--gl-text-color-default)] hover:bg-[var(--gl-background-color-subtle)]"
                  @click="close"
                >
                  {{ groupItem.text }}
                </a>
                <div
                  v-else
                  class="cursor-pointer px-4 py-2 text-sm text-[var(--gl-text-color-default)] hover:bg-[var(--gl-background-color-subtle)]"
                  @click="handleAction(groupItem)"
                >
                  {{ groupItem.text }}
                </div>
              </li>
            </ul>
          </template>

          <!-- Plain item -->
          <a
            v-else-if="item.href"
            :href="item.href"
            target="_blank"
            rel="noopener noreferrer"
            role="menuitem"
            class="block px-4 py-2 text-sm text-[var(--gl-text-color-default)] hover:bg-[var(--gl-background-color-subtle)]"
            @click="close"
          >
            {{ item.text }}
          </a>
          <div
            v-else
            role="menuitem"
            class="cursor-pointer px-4 py-2 text-sm text-[var(--gl-text-color-default)] hover:bg-[var(--gl-background-color-subtle)]"
            @click="handleAction(item)"
          >
            {{ item.text }}
          </div>
        </template>
      </slot>
    </div>
  </div>
</template>
